// Sugalarm — cycle 316: the first-run alarm-permissions onboarding tour.
//
// iOS + Android MOBILE SHELLS ONLY (never kiosk / plain web — the platform
// resolution below returns null everywhere else, and the App-level mount
// additionally guards). A full-screen paged walk through every urgent-path
// permission, per the approved 390x844 mockup: progress dots, a per-step
// Skip (44px), a "Set up later in Settings" ghost on the welcome screen,
// and a summary with the Sugalarm shield when everything is granted.
//
//   iOS (4):     Welcome → Turn on notifications → Allow Critical Alerts →
//                Done. Both grants are IN-APP system prompts, fired through
//                the cycle-316 AlarmReliability iOS implementation
//                (requestNotifications — the same UNUserNotificationCenter
//                pathway the app already uses; APNS registration untouched).
//   Android (6): Welcome → Allow notifications → Break through Do Not
//                Disturb → Show alarms over the lock screen (API 34+ only;
//                omitted with its dot below that = 5 steps) → Keep Sugalarm
//                awake → Summary. Deep-link steps drive the EXISTING
//                cycle-315 plugin methods; getStatus re-runs on app resume
//                so a grant made in system settings auto-advances the step.
//
// NON-INTERFERENCE (deliverable 6): while an alarm episode is live this
// component renders NOTHING — the alarm surface wins unconditionally; the
// tour's state stays mounted so it resumes after the episode clears.
// Skipping (whole tour or steps) stamps only the device-local done flag —
// the cycle-315 home banner + Alarm Reliability screen remain the
// persistent nag path. Nothing syncs through JSONB settings.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  getStatus as getAndroidStatus,
  getIosStatus,
  isAndroidPhoneNative,
  isIosPhoneNative,
  openAppNotificationSettings,
  openDndAccessSettings,
  openFullScreenIntentSettings,
  requestBatteryExemption,
  requestIosNotifications,
  type AlarmReliabilityStatus,
  type IosAlarmReliabilityStatus,
} from "../../lib/alarmReliability";
import {
  closeAlarmTour,
  isAlarmTourOpen,
  subscribeAlarmTour,
} from "../../lib/alarmTour";
import {
  deriveTourSteps,
  firstUnmetStep,
  stepGranted,
  unmetStepCount,
  type TourPlatform,
  type TourStepId,
} from "../../lib/alarmTourSteps";
import { requestPushPermission } from "../../lib/capacitor";
import { hapticSelect } from "../../lib/haptics";
import styles from "./AlarmOnboardingTour.module.css";

function tourPlatform(): TourPlatform | null {
  if (isIosPhoneNative()) return "ios";
  if (isAndroidPhoneNative()) return "android";
  return null;
}

// ── Per-step mockup copy (verbatim from onboarding_tour_mockup.html; the
//    one deviation: the iOS Critical Alerts why-card says "urgent glucose
//    alarms" instead of naming two tiers, since plain Low is also in the
//    critical set post-cycle-314 — the same accuracy fix as the cycle-315
//    checklist footer). No em dashes anywhere. ──

interface StepCopy {
  glyph: string;
  warnRing: boolean;
  title: string;
  body: ReactNode;
  why?: { icon: string; body: ReactNode };
  primary: string;
  ghost?: string;
}

const WELCOME_BODY =
  "Sugar Alarm watches glucose around the clock and wakes this phone the moment a reading turns dangerous.";

function stepCopy(
  step: TourStepId,
  platform: TourPlatform,
  isSamsung: boolean,
): StepCopy | null {
  if (step === "welcome") {
    return {
      glyph: "🔔",
      warnRing: false,
      title: "Alarms that never sleep",
      body: WELCOME_BODY,
      why:
        platform === "ios"
          ? {
              icon: "🛡",
              body: (
                <>
                  <b>Two quick permissions</b> make sure urgent alarms break
                  through silent mode, Focus, and Do Not Disturb. This takes
                  about 30 seconds.
                </>
              ),
            }
          : {
              icon: "🛡",
              body: (
                <>
                  Android needs <b>four quick permissions</b> so urgent alarms
                  break through Do Not Disturb, silent mode, and battery
                  saving. About a minute, and we&rsquo;ll walk you through
                  each one.
                </>
              ),
            },
      primary: "Get started",
      ghost: "Set up later in Settings",
    };
  }
  if (step === "notifications") {
    return {
      glyph: "💬",
      warnRing: true,
      title: platform === "ios" ? "Turn on notifications" : "Allow notifications",
      body: (
        <>
          Alerts for highs, lows, and system status all arrive as
          notifications. Tap <b>Allow</b> on the{" "}
          {platform === "ios" ? "next prompt" : "prompt"}.
        </>
      ),
      primary: "Enable notifications",
      ghost: "Not now",
    };
  }
  if (step === "critical") {
    return {
      glyph: "🚨",
      warnRing: true,
      title: "Allow Critical Alerts",
      body: (
        <>
          Critical Alerts are Apple&rsquo;s highest alarm level. They sound
          even when your iPhone is <b>muted</b> or in <b>Do Not Disturb</b>.
        </>
      ),
      why: {
        icon: "⚠",
        body: (
          <>
            Sugar Alarm uses Critical Alerts only for <b>urgent glucose alarms</b>.
            Nothing else will ever bypass your silent switch.
          </>
        ),
      },
      primary: "Enable Critical Alerts",
      ghost: "Not now",
    };
  }
  if (step === "dnd") {
    return {
      glyph: "🌙",
      warnRing: true,
      title: "Break through Do Not Disturb",
      body: (
        <>
          Android silences everything in Do Not Disturb unless you grant an
          exception. This opens a system page: find <b>Sugar Alarm</b> and switch
          on <b>Allow Do Not Disturb</b>.
        </>
      ),
      why: {
        icon: "⚠",
        body: (
          <>
            Only <b>urgent glucose alarms</b> use this. Routine notifications
            still respect Do Not Disturb.
          </>
        ),
      },
      primary: "Open system settings",
      ghost: "Not now",
    };
  }
  if (step === "fullscreen") {
    return {
      glyph: "📱",
      warnRing: true,
      title: "Show alarms over the lock screen",
      body: (
        <>
          Full-screen alarms turn the screen on and take over the display for
          an urgent reading, just like a morning alarm clock.
        </>
      ),
      why: {
        icon: "ⓘ",
        body: (
          <>
            Shown only on <b>Android 14 and newer</b>. On the system page,
            allow <b>full-screen notifications</b> for Sugar Alarm.
          </>
        ),
      },
      primary: "Open system settings",
      ghost: "Not now",
    };
  }
  if (step === "battery") {
    return {
      glyph: "🔋",
      warnRing: true,
      title: "Keep Sugar Alarm awake",
      body: (
        <>
          Battery savers can delay alarms by minutes. Tap <b>Allow</b> so
          Android never puts Sugar Alarm to sleep.
        </>
      ),
      // The Samsung "Sleeping apps" note (mockup AND-5 why-card) renders
      // only when getStatus reports isSamsung — see the render site.
      why: isSamsung
        ? {
            icon: "⚠",
            body: (
              <>
                <b>Samsung phones:</b> also open Settings &gt; Battery &gt;
                Background usage limits and make sure Sugar Alarm is not in the{" "}
                <b>Sleeping apps</b> list.
              </>
            ),
          }
        : undefined,
      primary: "Allow unrestricted battery",
      ghost: "Not now",
    };
  }
  return null;
}

const ANDROID_ROW_LABELS: Partial<Record<TourStepId, string>> = {
  notifications: "Notifications enabled",
  dnd: "Override Do Not Disturb",
  fullscreen: "Full-screen alarms",
  battery: "Battery unrestricted",
};

const IOS_ROW_LABELS: Partial<Record<TourStepId, string>> = {
  notifications: "Notifications enabled",
  critical: "Critical Alerts allowed",
};

export default function AlarmOnboardingTour({
  episodeLive,
  noDataAlarmActive = false,
}: {
  episodeLive: boolean;
  /** Cycle 453.2 (operator decision 3): a No Data outage alarm is up. The
   *  tour paints outside the frame (z 8500), above every alarm control in
   *  it, so it steps aside exactly as it does for a live glucose alarm. */
  noDataAlarmActive?: boolean;
}) {
  const open = useSyncExternalStore(subscribeAlarmTour, isAlarmTourOpen);
  const platform = tourPlatform();

  const [android, setAndroid] = useState<AlarmReliabilityStatus | null>(null);
  const [ios, setIos] = useState<IosAlarmReliabilityStatus | null>(null);
  const [index, setIndex] = useState(0);

  const refresh = useCallback(() => {
    if (platform === "android") {
      void getAndroidStatus().then((s) => {
        if (s != null) setAndroid(s);
      });
    } else if (platform === "ios") {
      void getIosStatus().then((s) => {
        if (s != null) setIos(s);
      });
    }
  }, [platform]);

  // Fresh state each time the tour opens.
  useEffect(() => {
    if (!open) return;
    setIndex(0);
    refresh();
  }, [open, refresh]);

  // Re-run getStatus on app resume/foreground so a grant made on a system
  // settings page flips the current step on return (the auto-advance
  // effect below then moves the flow forward).
  useEffect(() => {
    if (!open) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [open, refresh]);

  const steps = useMemo(
    () => (platform ? deriveTourSteps(platform, android) : []),
    [platform, android],
  );
  const current: TourStepId | null =
    steps.length > 0 ? steps[Math.min(index, steps.length - 1)]! : null;

  // Auto-advance: a permission step whose grant is (or becomes) met moves
  // on by itself — covers both "returned from system settings with the
  // grant made" (the resume re-read above) and "this permission was
  // already granted".
  useEffect(() => {
    if (!open || platform == null || current == null) return;
    if (current === "welcome" || current === "summary") return;
    if (stepGranted(current, platform, android, ios)) {
      setIndex((i) => Math.min(i + 1, steps.length - 1));
    }
  }, [open, platform, current, android, ios, steps.length]);

  if (!open || platform == null || current == null) return null;
  // Deliverable 6 — the alarm path wins unconditionally: a live episode
  // renders the alarm surfaces, not the tour (state stays mounted, so the
  // tour resumes where it was once the episode clears).
  if (episodeLive || noDataAlarmActive) return null;

  const advance = () => setIndex((i) => Math.min(i + 1, steps.length - 1));

  const skipStep = () => {
    hapticSelect();
    advance();
  };

  const dismiss = () => {
    hapticSelect();
    closeAlarmTour(true);
  };

  const firePrimary = async () => {
    hapticSelect();
    switch (current) {
      case "welcome":
        advance();
        return;
      case "notifications":
        if (platform === "ios") {
          const r = await requestIosNotifications(false);
          if (r != null) setIos(r);
          advance();
        } else {
          // Fire the real POST_NOTIFICATIONS prompt via the existing
          // helper; if it was previously hard-denied (no prompt shows),
          // fall through to this app's notification settings page.
          const result = await requestPushPermission();
          if (result === "denied") {
            void openAppNotificationSettings();
          }
          refresh();
        }
        return;
      case "critical": {
        const r = await requestIosNotifications(true);
        if (r != null) setIos(r);
        advance();
        return;
      }
      case "dnd":
        void openDndAccessSettings();
        return;
      case "fullscreen":
        void openFullScreenIntentSettings();
        return;
      case "battery":
        void requestBatteryExemption();
        // The exemption dialog resolves in-app on some OEMs (no app
        // leave) — poll once shortly after so the grant auto-advances
        // without needing a resume event.
        window.setTimeout(refresh, 800);
        return;
      case "summary":
        return;
    }
  };

  // ── Summary derivations ──
  const unmet = unmetStepCount(steps, platform, android, ios);
  const allGranted = unmet === 0;
  const rowLabels = platform === "ios" ? IOS_ROW_LABELS : ANDROID_ROW_LABELS;
  const rowSteps = steps.filter((s) => rowLabels[s] != null);
  const fixTarget = firstUnmetStep(steps, platform, android, ios);
  const singleSkippedIsDnd =
    unmet === 1 && fixTarget === "dnd" && platform === "android";

  const copy =
    current !== "summary"
      ? stepCopy(current, platform, android?.isSamsung === true)
      : null;

  return (
    <div className={styles.root} role="dialog" aria-label="Alarm setup">
      <div className={styles.topbar}>
        <div className={styles.dots} aria-hidden>
          {steps.map((s, i) => (
            <span
              key={s}
              className={`${styles.dot} ${i <= index ? styles.dotOn : ""}`}
            />
          ))}
        </div>
        {current !== "summary" ? (
          <button
            type="button"
            className={styles.skip}
            aria-label="Skip this step"
            onClick={skipStep}
          >
            Skip
          </button>
        ) : (
          <span className={styles.skipSpacer} />
        )}
      </div>

      {current === "summary" ? (
        <>
          {allGranted ? (
            <div className={styles.shieldHero}>
              <div className={styles.shieldWrap}>
                <div className={styles.shieldGlow} aria-hidden />
                <img
                  className={styles.shieldImg}
                  src="/sugar-alarm-mark.png"
                  alt="Sugar Alarm shield"
                />
              </div>
            </div>
          ) : (
            <div className={styles.hero}>
              <div className={`${styles.glyph} ${styles.glyphWarn}`} aria-hidden>
                🔔
              </div>
            </div>
          )}
          <div className={styles.copy}>
            <h1>{allGranted ? "You’re protected" : "Almost protected"}</h1>
            <p>
              {allGranted
                ? platform === "ios"
                  ? "Urgent alarms will now wake this iPhone no matter what."
                  : "Urgent alarms will now wake this phone no matter what."
                : singleSkippedIsDnd
                  ? "One protection was skipped. Urgent alarms may stay silent during Do Not Disturb until it’s granted."
                  : unmet === 1
                    ? "One protection was skipped. Urgent alarms may not reach you until it’s granted."
                    : "Some protections were skipped. Urgent alarms may not reach you until they’re granted."}
            </p>
          </div>
          <div
            className={`${styles.pill} ${allGranted ? styles.pillOk : styles.pillWarn}`}
          >
            <span aria-hidden>{allGranted ? "✓" : "⚠"}</span>{" "}
            {allGranted
              ? "All protections active"
              : `${unmet} protection${unmet === 1 ? "" : "s"} need${
                  unmet === 1 ? "s" : ""
                } setup`}
          </div>
          <div className={styles.sumlist}>
            {rowSteps.map((s) => {
              const ok = stepGranted(s, platform, android, ios);
              return (
                <div key={s} className={styles.sumrow}>
                  <span
                    className={`${styles.st} ${ok ? styles.stOk : styles.stWarn}`}
                    aria-hidden
                  >
                    {ok ? "✓" : "!"}
                  </span>
                  {rowLabels[s]}
                  {!ok ? <span className={styles.later}>Skipped</span> : null}
                </div>
              );
            })}
          </div>
          {allGranted && platform === "ios" ? (
            <div className={styles.why}>
              <span className={styles.whyIcon} aria-hidden>
                ⓘ
              </span>
              <div>
                You can review this anytime in <b>Settings &gt; Alarm
                Reliability</b>.
              </div>
            </div>
          ) : null}
          <div className={styles.spacer} />
          <div className={styles.actions}>
            {allGranted || platform === "ios" ? (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={dismiss}
              >
                Start monitoring
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => {
                    hapticSelect();
                    if (fixTarget != null) {
                      setIndex(Math.max(steps.indexOf(fixTarget), 0));
                    }
                  }}
                >
                  Fix now
                </button>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={dismiss}
                >
                  Continue anyway
                </button>
              </>
            )}
          </div>
        </>
      ) : copy != null ? (
        <>
          <div className={styles.hero}>
            <div
              className={`${styles.glyph} ${copy.warnRing ? styles.glyphWarn : ""}`}
              aria-hidden
            >
              {copy.glyph}
            </div>
          </div>
          <div className={styles.copy}>
            <h1>{copy.title}</h1>
            <p>{copy.body}</p>
          </div>
          {copy.why ? (
            <div className={styles.why}>
              <span className={styles.whyIcon} aria-hidden>
                {copy.why.icon}
              </span>
              <div>{copy.why.body}</div>
            </div>
          ) : null}
          <div className={styles.spacer} />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => {
                void firePrimary();
              }}
            >
              {copy.primary}
            </button>
            {copy.ghost ? (
              <button
                type="button"
                className={styles.btnGhost}
                onClick={current === "welcome" ? dismiss : skipStep}
              >
                {copy.ghost}
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
